import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { Asset, NodeLabel, Operator, Port, TreeNode } from '@/types';
import { QueryStatus } from '@/utils/api';

/**
 * One row per (member-IP, port) pair, mirroring the `ImpactedAssetPort` shape
 * the Technology drawer uses for its Assets Impacted tab. IPs with no ports
 * still surface as a single row with `port === undefined`.
 */
export interface MemberAssetPort {
  asset: Asset;
  port?: Port;
  /** Most-recent activity timestamp: port.visited when present, else asset.visited. */
  visited: string;
}

export interface UseApplicationMembersResult {
  /** (Asset, Port) pairs rolled up under this Application. */
  memberAssetPorts: MemberAssetPort[];
  /** WebApplication observations rolled up under this Application. */
  memberWebApplications: Asset[];
  /** Status of the (asset, port) tree query. */
  assetsStatus: QueryStatus;
  /** Status of the web-applications query. */
  webApplicationsStatus: QueryStatus;
  /** Error from either sub-query, surfaced for Table.error. */
  assetsError: Error | null;
  webApplicationsError: Error | null;
}

/**
 * Fetch the observation nodes (Asset + WebApplication) rolled up under an
 * Application via HAS_MEMBER, plus the ports hanging off each member IP via
 * HAS_PORT. The asset side uses a tree query in the same shape as
 * `useQueryTechnologyAssets` so the result feeds the shared Table component
 * with one row per (asset, port) pair.
 *
 * Queries are gated on `applicationKey`; the standard asset drawer code path
 * is unaffected when the parent asset isn't an Application.
 */
export function useApplicationMembers(
  applicationKey: string
): UseApplicationMembersResult {
  const enabled = Boolean(applicationKey);

  // Tree query: Application -[HAS_MEMBER]-> Asset -[HAS_PORT(optional)]-> Port
  const {
    data: memberAssetTree = [],
    status: assetsStatus,
    error: assetsError,
  } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Asset'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_MEMBER',
              source: {
                labels: ['Application'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: applicationKey,
                  },
                ],
              },
            },
            {
              label: 'HAS_PORT',
              optional: true,
              target: { labels: ['Port'] as NodeLabel[] },
            },
          ],
        },
      },
    },
    { enabled }
  );

  // Flat query: WebApplications under the Application.
  const {
    data: memberWebApplications = [],
    status: webApplicationsStatus,
    error: webApplicationsError,
  } = useMy(
    {
      resource: 'webapplication',
      query: {
        node: {
          labels: ['WebApplication'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_MEMBER',
              source: {
                labels: ['Application'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: applicationKey,
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled }
  );

  // Flatten the tree into one row per (asset, port). Assets with no ports
  // produce a single row with port=undefined so empty IPs still appear.
  const memberAssetPorts = useMemo<MemberAssetPort[]>(() => {
    const rows: MemberAssetPort[] = [];
    for (const treeNode of memberAssetTree) {
      const asset = (treeNode?.node?.result?.assets?.[0] ?? undefined) as
        | Asset
        | undefined;
      if (!asset) continue;

      const portRels =
        (treeNode as TreeNode)?.node?.relationships?.filter(
          rel => rel?.label === 'HAS_PORT'
        ) ?? [];
      const ports = portRels
        .flatMap(
          rel => (rel?.target?.result?.ports as Port[] | undefined) ?? []
        )
        .filter(Boolean);

      if (ports.length === 0) {
        rows.push({ asset, port: undefined, visited: asset.visited || '' });
        continue;
      }
      for (const port of ports) {
        rows.push({
          asset,
          port,
          visited: port.visited || asset.visited || '',
        });
      }
    }
    return rows;
  }, [memberAssetTree]);

  return {
    memberAssetPorts,
    memberWebApplications,
    assetsStatus,
    webApplicationsStatus,
    assetsError: assetsError ?? null,
    webApplicationsError: webApplicationsError ?? null,
  };
}
